import { type FC, useCallback, useState } from "react";
import { Presentation } from "@clientFrontend/features/publication/components/SingleImageSelectField/Presentation";
import {
  type ClientImageResponse,
  type DisplayVariant,
  type Props,
} from "@clientFrontend/features/publication/components/SingleImageSelectField/types";
import { type ClientImage } from "@clientFrontend/features/publication/components/ClientImageLibraryModal/type";
import { axios } from "@root/libs/axios";
import { sendErrorLog } from "@root/shared/utils/sendErrorLog";
import { type ApiErrorResponse } from "@root/shared/types/type";
import { isApiError } from "@root/shared/utils/typeGuard";

// 選択画像を表示・反映するURLを、画面が指定したバリアントに従って解決する
// square は 1:1 の squareImageUrl(medium)。未取得(album 初期表示など)の場合は imageUrl にフォールバック
const resolveDisplayUrl = (image: ClientImage, variant: DisplayVariant): string =>
  variant === "square" ? (image.squareImageUrl ?? image.imageUrl) : image.imageUrl;

export const SingleImageSelectField: FC<Props> = ({
  hiddenFieldName,
  displayVariant,
  clientImageUrl,
  initialSelectedImage,
  placeholderImageUrl,
  headerTitle,
  errorMessages = [],
}) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [clientImages, setClientImages] = useState<ClientImage[]>([]);
  const [selectedImage, setSelectedImage] = useState<ClientImage | null>(initialSelectedImage);

  // モーダルを開くたびに一覧を取得する。新規画像はアップロード時にサーバー永続化されるため、
  // 他モーダルでの追加分もこの取得で反映される(コンポーネント間の共有ストアは不要)
  const fetchClientImages = useCallback(async () => {
    try {
      const { data } = await axios.get<ClientImageResponse | ApiErrorResponse>(clientImageUrl);
      if (isApiError(data)) {
        sendErrorLog(new Error(data.message || "画像の取得に失敗しました"));
        return;
      }
      // IDの降順でソートし、最新の画像が一番上に来るようにする
      const newClientImages: ClientImage[] = data.data
        .sort((a, b) => b.id - a.id)
        .map((clientImage) => ({
          id: clientImage.id,
          filename: clientImage.name,
          imageUrl: clientImage.urls.minimum,
          squareImageUrl: clientImage.urls.medium,
        }));
      setClientImages(newClientImages);
    } catch (error) {
      sendErrorLog(new Error(error instanceof Error ? error.message : "画像の取得に失敗しました"));
    }
  }, [clientImageUrl]);

  // 単一選択なので確定時の選択画像は 0 or 1 枚。未選択で確定した場合は画像を解除する
  const handleConfirm = useCallback((selectedImages: ClientImage[]) => {
    setSelectedImage(selectedImages[0] ?? null);
  }, []);

  const displayImageUrl = selectedImage
    ? resolveDisplayUrl(selectedImage, displayVariant)
    : placeholderImageUrl;

  return (
    <Presentation
      hiddenFieldName={hiddenFieldName}
      clientImageUrl={clientImageUrl}
      headerTitle={headerTitle}
      errorMessages={errorMessages}
      selectedImage={selectedImage}
      displayImageUrl={displayImageUrl}
      displayVariant={displayVariant}
      showModal={showModal}
      clientImages={clientImages}
      handleOpenModal={() => setShowModal(true)}
      handleCloseModal={() => setShowModal(false)}
      fetchClientImages={fetchClientImages}
      setClientImages={setClientImages}
      handleConfirm={handleConfirm}
    />
  );
};
